import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  fetchNotifications,
  markAllNotificationsRead,
  markNotificationRead,
  type Notification,
} from '../services/notificationsApi';
import { WS_BASE } from '../services/api';
import { handleWsClose } from '../lib/orgBlock';
import { wsReconnectDelayMs, wsShouldGiveUp } from '../lib/wsReconnect';
import { isWsSessionExpired, onSessionRenewed, renewWsSession } from '../lib/wsSession';

/** `WebSocket.CONNECTING` / `.OPEN` kot literala — testni dvojnik statik nima. */
const WS_CONNECTING = 0;
const WS_OPEN = 1;

export interface UseNotificationsOptions {
  /**
   * T3124: obvestilo je prišlo V ŽIVO med sejo — po WebSocketu ali kot novo pri
   * osveževanju prve strani (anketiranje, ko WS ni povezan). Začetno branje ob
   * prijavi in starejše strani (»Naloži več«) niso prihod: ob prijavi je
   * ponudnik s 56 neprebranimi sicer dobil šest toastov iz prejšnjega dne.
   */
  onLive?: (notification: Notification) => void;
}

export function useNotifications({ onLive }: UseNotificationsOptions = {}) {
  // T1412: strežnik besedilo obvestila izriše v jeziku prejemnika — ob zamenjavi
  // jezika seznam naložimo znova, sicer zvonec ostane v starem jeziku.
  const { i18n } = useTranslation();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  // T1255: povezavo je zaledje zaprlo s terminalno kodo (4003/4004/4005).
  // To ni prehodno stanje: ne ponovni poskus ne anketiranje nimata smisla, dokler
  // se aplikacija ne osveži oziroma zaklep ne odpade na strani zaledja.
  const [isTerminated, setIsTerminated] = useState(false);

  const socketRef = useRef<WebSocket | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const backoffIndexRef = useRef(0);
  const mountedRef = useRef(true);
  // T1630: zaporedne zavrnitve rokovanja (`onopen` ni bil klican) in ali se je
  // TRENUTNA povezava kdaj odprla. Povezava, ki je padla po odprtju, je izpad
  // omrežja — števec ponastavi in backoff začne od začetka.
  const handshakeFailuresRef = useRef(0);
  const openedRef = useRef(false);
  // T1255: terminalna close koda je dokončna in je `retryNow` ne sme obuditi.
  const terminatedRef = useRef(false);
  // T1779: `id` obvestil, ki jih seznam že nosi (iz HTTP ali WS). Ref in ne
  // izpeljava iz stanja, ker števec ne sme narasti dvakrat niti takrat, ko dve
  // enaki sporočili prideta pred naslednjim izrisom.
  const seenIdsRef = useRef<Set<string>>(new Set());
  // T3124: prvo uspešno branje je izhodišče — kar prinese, ni prihod v živo.
  const baselineLoadedRef = useRef(false);
  const onLiveRef = useRef(onLive);
  useEffect(() => {
    onLiveRef.current = onLive;
  }, [onLive]);

  const loadPage = useCallback(async (page: number, append: boolean) => {
    try {
      const data = await fetchNotifications({ page });
      if (!mountedRef.current) return;
      // T3124: na prvi strani po izhodišču je neznano obvestilo nov prihod.
      // Starejše strani (append) prihodov ne nosijo.
      const arrivals =
        baselineLoadedRef.current && !append
          ? data.results.filter((n) => !seenIdsRef.current.has(n.id))
          : [];
      if (!append) baselineLoadedRef.current = true;
      data.results.forEach((n) => seenIdsRef.current.add(n.id));
      arrivals.forEach((n) => onLiveRef.current?.(n));
      setNotifications((prev) => (append ? [...prev, ...data.results] : data.results));
      setUnreadCount(data.unread_count);
      setHasMore(data.next !== null);
      setCurrentPage(page);
    } finally {
      if (mountedRef.current) setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    setIsLoading(true);
    loadPage(1, false);
  }, [loadPage, i18n.language]);

  // Polling fallback: refresh every 30s when WebSocket is disconnected.
  // T1255: ob terminalno zaprti povezavi ga NE zaganjamo. Sicer je popravek le
  // polovičen — zaklenjena organizacija bi namesto dveh neuspešnih klicev na
  // 30 s proizvajala enega, prav tako v nedogled (403 ORGANIZATION_SUSPENDED).
  useEffect(() => {
    if (isTerminated) return;
    const id = setInterval(() => {
      if (!isConnected && mountedRef.current) {
        loadPage(1, false);
      }
    }, 30_000);
    return () => clearInterval(id);
  }, [isConnected, isTerminated, loadPage]);

  const connect = useCallback(() => {
    if (!mountedRef.current) return;

    const wsUrl = `${WS_BASE}/notifications/`;
    const socket = new WebSocket(wsUrl);
    socketRef.current = socket;
    openedRef.current = false;

    // T1779: vsak ročaj najprej preveri, ali je ta vtičnica še TRENUTNA.
    // Zapiranje vtičnice v stanju CONNECTING je v brskalniku asinhrono, zato
    // `onclose` stare vtičnice pride šele PO ponovni montaži (React StrictMode
    // v razvoju montira → odmontira → montira), ko je `mountedRef` spet `true`.
    // Brez te preverbe je stara vtičnica ob svojem zaprtju sprožila ponovno
    // povezovanje, nova pa je ostala odprta — dve živi povezavi v istem
    // zavihku, zato je vsako obvestilo prišlo dvakrat (dve enaki kartici
    // »Zunanji sodelavec čaka na odobritev«, ročna verifikacija §4.14).
    const isCurrent = () => socketRef.current === socket;

    socket.onopen = () => {
      if (!mountedRef.current || !isCurrent()) {
        socket.close();
        return;
      }
      openedRef.current = true;
      setIsConnected(true);
      backoffIndexRef.current = 0;
      handshakeFailuresRef.current = 0;
    };

    socket.onmessage = (event) => {
      if (!mountedRef.current || !isCurrent()) return;
      let msg: { type: string; payload: Notification };
      try {
        msg = JSON.parse(event.data as string) as { type: string; payload: Notification };
      } catch {
        console.warn('[useNotifications] malformed WS message:', event.data);
        return;
      }
      if (msg.type === 'notification.new') {
        // T1779: obramba v globino — obvestilo, ki ga seznam že ima (npr. ker
        // ga je prinesel že HTTP odziv ob montaži), se ne doda drugič in ne
        // poveča števca. `NotificationBell` sicer za vsako pojavitev istega
        // `id` izriše svoj toast.
        const incoming = msg.payload;
        if (seenIdsRef.current.has(incoming.id)) return;
        seenIdsRef.current.add(incoming.id);
        setNotifications((prev) =>
          prev.some((n) => n.id === incoming.id) ? prev : [incoming, ...prev]
        );
        setUnreadCount((prev) => prev + 1);
        onLiveRef.current?.(incoming);
      }
    };

    socket.onclose = (event: CloseEvent) => {
      // T1779: zaprtje zamenjane vtičnice ni izpad trenutne povezave — ne
      // spreminja stanja in ne sproži ponovnega povezovanja.
      if (!mountedRef.current || !isCurrent()) return;
      setIsConnected(false);

      // T2689: 4001 po `accept()` = dostopni žeton je potekel (T382), ne trajna
      // zavrnitev. Sejo obnovimo prek HTTP (tiha osvežitev ali okno za ponovno
      // prijavo) in se šele nato povežemo znova — s svežim piškotkom. Ob
      // odjavi/preklicu obljuba pade in kanal ostane zaprt; anketiranje teče.
      if (isWsSessionExpired(event)) {
        void renewWsSession().then(
          () => {
            if (!mountedRef.current || terminatedRef.current || !isCurrent()) return;
            handshakeFailuresRef.current = 0;
            backoffIndexRef.current = 0;
            connect();
          },
          () => {},
        );
        return;
      }

      // T1255: zaledje (`apps/core/ws_suspension.py`) zavrnitev sporoči s close
      // kodo in izrecno pravi »ne poskušaj znova«. Ta hook je montiran v lupini
      // za vsakega prijavljenega uporabnika, backoff pa je bil zamejen navzgor
      // (30 s) in ne navzdol — brez te veje se poskusi ne ustavijo nikoli.
      // Ob 4004/4005 `handleWsClose` javi zaklep v isti mehanizem kot HTTP 403.
      if (handleWsClose(event)) {
        terminatedRef.current = true;
        setIsTerminated(true);
        return;
      }

      // T1630: kode 4004/4005 do brskalnika ne pridejo — zaledje zavrne PRED
      // `accept()`, zato je vse, kar tu vidimo, 1006. Zato zamejimo zaporedne
      // zavrnjene poskuse rokovanja. Anketiranje na 30 s ob tem NAMENOMA teče
      // naprej (`isTerminated` ostane `false`): tu ne vemo, da je zavrnitev
      // trajna, HTTP pot pa ob pravem zaklepu prevzame zaslon sama.
      handshakeFailuresRef.current = openedRef.current ? 0 : handshakeFailuresRef.current + 1;
      if (wsShouldGiveUp(handshakeFailuresRef.current)) return;

      const delay = wsReconnectDelayMs(backoffIndexRef.current);
      backoffIndexRef.current += 1;
      reconnectTimerRef.current = setTimeout(() => {
        if (mountedRef.current) connect();
      }, delay);
    };
  }, []);

  /**
   * T1630: odnehanje ni dokončno — ob `online` in ob vrnitvi zavihka v ospredje
   * poskuse ponastavimo. Brez tega bi prebujen prenosnik ostal brez obvestil v
   * živo do osveževanja strani.
   */
  const retryNow = useCallback(() => {
    if (!mountedRef.current || terminatedRef.current) return;
    const state = socketRef.current?.readyState;
    if (state === WS_OPEN || state === WS_CONNECTING) return;
    if (reconnectTimerRef.current !== null) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    handshakeFailuresRef.current = 0;
    backoffIndexRef.current = 0;
    connect();
  }, [connect]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') retryNow();
    };
    window.addEventListener('online', retryNow);
    document.addEventListener('visibilitychange', onVisible);
    // T2689: uspešna ponovna prijava v oknu (T2376) obudi kanal, ki je med
    // potekom seje odnehal.
    const unsubscribeRenewed = onSessionRenewed(retryNow);
    return () => {
      unsubscribeRenewed();
      window.removeEventListener('online', retryNow);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [retryNow]);

  useEffect(() => {
    mountedRef.current = true;
    connect();
    return () => {
      mountedRef.current = false;
      if (reconnectTimerRef.current !== null) {
        clearTimeout(reconnectTimerRef.current);
      }
      socketRef.current?.close();
    };
  }, [connect]);

  const markRead = useCallback((id: string) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, is_read: true, read_at: new Date().toISOString() } : n))
    );
    setUnreadCount((prev) => Math.max(0, prev - 1));
    markNotificationRead(id).catch(() => {
      if (!mountedRef.current) return;
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, is_read: false, read_at: null } : n))
      );
      setUnreadCount((prev) => prev + 1);
    });
  }, []);

  const markAllRead = useCallback(() => {
    setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true, read_at: new Date().toISOString() })));
    setUnreadCount(0);
    markAllNotificationsRead().catch(() => {
      if (!mountedRef.current) return;
      loadPage(currentPage, false);
    });
  }, [currentPage, loadPage]);

  const loadMore = useCallback(() => {
    if (!hasMore) return;
    loadPage(currentPage + 1, true);
  }, [hasMore, currentPage, loadPage]);

  return {
    notifications,
    unreadCount,
    isConnected,
    isLoading,
    hasMore,
    markRead,
    markAllRead,
    loadMore,
  };
}
